<script setup lang="ts">
const paginationDefaults = usePaginationDefaults();

const props = withDefaults(
  defineProps<{
    providerKey: string;
    totalItems: number;
    itemType?: string;
    itemTypePlural?: string;
    paginationId?: string;
    pageQueryKey?: string;

    initialPage?: number;
    pageSizeQueryKey?: string;
    pageSizeOptions?: number[];
    defaultPageSize?: number;
    initialPageSize?: number;

    showIfSinglePage?: boolean;
    showFirstAndLastButtons?: boolean;
    showPageSizeChange?: boolean;
    useEllipsis?: boolean;
    lockFirstAndLastPages?: boolean;
    maxVisiblePageButtons?: number;
    loadingStatus?: PaginationLoadingObject[];

    theme?: Partial<PaginationTheme> | (() => Partial<PaginationTheme>);
  }>(),
  {
    providerKey: undefined,
    totalItems: undefined,
    itemType: undefined,
    itemTypePlural: undefined,
    paginationId: undefined,
    pageQueryKey: undefined,

    initialPage: 1,
    pageSizeQueryKey: undefined,
    pageSizeOptions: undefined,
    defaultPageSize: undefined,
    initialPageSize: undefined,

    showIfSinglePage: true,
    showFirstAndLastButtons: true,
    showPageSizeChange: true,
    useEllipsis: false,
    lockFirstAndLastPages: false,
    maxVisiblePageButtons: 5,
    loadingStatus: undefined,

    theme: () => ({})
  }
);

const emit = defineEmits<{
  'update:page': [EmitPageChange];
  'update:pageSize': [EmitPageSizeChange];
}>();

const { tags } = useFilters();

// --- Theme ---
const resolvedTheme = typeof props.theme === 'function' ? props.theme() : props.theme;
providePaginationTheme(resolvedTheme ?? {});

// --- Defaults / Page size options ---
const resolvedDefaultPageSize = props.defaultPageSize ?? paginationDefaults.defaultPageSize;
const basePageSize =
  props.initialPageSize && props.initialPageSize !== resolvedDefaultPageSize
    ? props.initialPageSize
    : resolvedDefaultPageSize;

const resolvedPageSizeOptions: number[] = props.pageSizeOptions ?? [basePageSize, basePageSize * 2, basePageSize * 5];

const { initialPage, initialPageSize } = usePaginationInitialState({
  defaultPageSize: resolvedDefaultPageSize,
  initialPage: props.initialPage ?? 1,
  initialPageSize: props.initialPageSize ?? resolvedDefaultPageSize,
  pageQueryKey: props.pageQueryKey,
  pageSizeQueryKey: props.pageSizeQueryKey
});

// --- Reactive state ---
const currentPageSize = ref(initialPageSize);
const totalItems = computed(() => {
  return props.totalItems;
});
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / currentPageSize.value)));
const currentPage = ref(Math.min(initialPage, totalPages.value));
const _pageSizeOptions = shallowRef(resolvedPageSizeOptions);
const latestClickedButton = ref<HTMLElement | null>(null);
const paginationCausedLoading = computed(() => {
  return Boolean(props.loadingStatus?.find((status) => status.type === 'page' && status.state));
});

// --- Derived ---
const itemType = computed(() => props.itemType ?? paginationDefaults.itemType);
const itemTypePlural = computed(() => props.itemTypePlural ?? paginationDefaults.itemTypePlural);

const isFirstDisabled = computed(() => currentPage.value === 1);
const isPrevDisabled = isFirstDisabled;
const isNextDisabled = computed(() => currentPage.value === totalPages.value);
const isLastDisabled = isNextDisabled;
const shouldDisplayPagination = computed(
  () => props.totalItems > 0 && (props.showIfSinglePage || totalPages.value > 1)
);

// --- Pages ---
const displayPages = usePaginationPages(
  currentPage,
  totalPages,
  ref(props.maxVisiblePageButtons),
  ref(props.useEllipsis),
  ref(props.lockFirstAndLastPages)
);

// --- Actions ---
function goToPage(page: number) {
  const clamped = Math.max(1, Math.min(page, totalPages.value));
  if (clamped !== currentPage.value) {
    currentPage.value = clamped;
    emit('update:page', { providerKey: props.providerKey, page: clamped, pageSize: currentPageSize.value, tags });
  }
}

function updatePageSize(size: number) {
  const minSize = resolvedPageSizeOptions[0] ?? paginationDefaults.defaultPageSize;
  const maxSize = resolvedPageSizeOptions[resolvedPageSizeOptions.length - 1] ?? paginationDefaults.defaultPageSize;
  const clamped = Math.max(minSize, Math.min(size, maxSize));

  if (clamped !== currentPageSize.value) {
    currentPageSize.value = clamped;
    goToPage(1);
    emit('update:pageSize', { providerKey: props.providerKey, size: clamped });
  }
}

function setPageSize(size: number) {
  updatePageSize(size);
}

function setLatestClickedButton(element: HTMLElement) {
  latestClickedButton.value = element;
}

// --- Watchers ---
watch([currentPageSize, totalItems], () => {
  currentPage.value = Math.min(currentPage.value, totalPages.value);
});

watch(
  () => props.initialPage,
  () => {
    currentPage.value = props.initialPage;
  }
);

// --- Query sync ---
usePaginationQuerySync(
  currentPage,
  currentPageSize,
  computed(() => props.pageQueryKey ?? paginationDefaults.pageQueryKey),
  computed(() => props.pageSizeQueryKey ?? paginationDefaults.pageSizeQueryKey),
  resolvedDefaultPageSize,
  totalPages
);
usePaginationCanonical(
  currentPage,
  currentPageSize,
  computed(() => props.pageQueryKey ?? paginationDefaults.pageQueryKey),
  computed(() => props.pageSizeQueryKey ?? paginationDefaults.pageSizeQueryKey),
  resolvedDefaultPageSize
);

// --- Provide context ---
const context: PaginationContext = {
  currentPage,
  pageQueryKey: computed(() => props.pageQueryKey ?? paginationDefaults.pageQueryKey),
  itemType,
  itemTypePlural,
  pageSizeQueryKey: computed(() => props.pageSizeQueryKey ?? paginationDefaults.pageSizeQueryKey),
  currentPageSize,
  defaultPageSize: resolvedDefaultPageSize,
  pageSizeOptions: _pageSizeOptions,
  latestClickedButton,
  paginationCausedLoading,
  totalItems,
  totalPages,
  displayPages,
  goToPage,
  nextPage: () => goToPage(currentPage.value + 1),
  prevPage: () => goToPage(currentPage.value - 1),
  firstPage: () => goToPage(1),
  lastPage: () => goToPage(totalPages.value),
  setPageSize,
  setLatestClickedButton,
  isFirstDisabled,
  isPrevDisabled,
  isNextDisabled,
  isLastDisabled,
  showFirstAndLastButtons: props.showFirstAndLastButtons,
  allowPageSizeChange: props.showPageSizeChange,
  paginationId: computed(() => props.paginationId ?? paginationDefaults.paginationId)
};

provide(PaginationContextKey, context);
</script>

<template>
  <section
    v-if="shouldDisplayPagination"
    class="pagination"
  >
    <slot />
    <slot name="announcer">
      <PaginationAnnouncer />
    </slot>
    <slot name="navigation">
      <PaginationDefault />
    </slot>
  </section>
</template>
